import { Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommitteeItem, isRelatedToApprovalType } from './committee-item';
import { CommitteeApprovalType } from '../committee-approvals/committee-approval-type';
import { CommitteeItemsService } from './committee-items.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import moment from 'moment';
import { Mutable } from '../../types/mutable';
import { MatTableDataSource } from '@angular/material/table';
import Milestone from '../../interfaces/milestone';
import { UserPermissionsService } from 'src/app/services/user-permissions.service';
import { CommitteeApprovalsService } from '../committee-approvals/committee-approvals.service';
import {AlertService} from "../../alert/alert.service";
import ID from "../../types/id";
import {MatSelectChange} from "@angular/material/select";
@Component({
    selector: 'app-committee-items',
    templateUrl: './committee-items.component.html',
    styleUrls: ['./committee-items.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommitteeItemsComponent implements OnInit {
  @Input() deal: any;
  @Input('projectId') projectId: string;
  @Input('approvalType') approvalType: CommitteeApprovalType;
  @Input('headerColumns') headerColumns: string[];
  @Input('milestones') milestones: Milestone[] = [];
  @Input('milestone') milestone: Milestone;
  @Input('milestoneName') milestoneName?: string;
  @Input('isLegalNote') isLegalNote: boolean = false;
  @Input('milestoneContext') milestoneContext?: string;
  @Input('isConditions') isConditions: boolean = false;
  @Input() approvalTypes?: CommitteeApprovalType[] = [];
  @Input('isCompleteConditions') isCompleteConditions: boolean = false;
  @Input() readOnly: boolean;

  public items: Mutable<Mutable<CommitteeItem>[]>;
  public service: CommitteeItemsService;
  public console: Console;
  public tableSource: MatTableDataSource<Mutable<CommitteeItem>> =
    new MatTableDataSource<Mutable<CommitteeItem>>([]);
  private itemsSub: Subscription;
  private milestoneIndeces: { [key: string]: number } = {};
  public isClosingItem: boolean = false;
  canEdit = false
  canEditOther = false;
  public legalPermissions: boolean = false;
  private relatedDepartmentSelections = new Map<string, CommitteeApprovalType[]>()

  constructor(
    private itemsService: CommitteeItemsService,
    private perms: UserPermissionsService,
    public permsService: UserPermissionsService,
    private approvalService: CommitteeApprovalsService,
    private alertService: AlertService
  ) {}
  ngOnInit() {
    this.milestoneIndeces['_'] = 100;
    if(this.milestoneName!==undefined && this.milestoneName=='Closed'){
      this.isClosingItem = true;
    }
    this.milestones.map((milestone, index) => {
      this.milestoneIndeces[milestone.id] = index;
      return milestone;
    });
    this.service = this.itemsService;
    //@deprecated: All parent components should request the columns they want.
    if (!this.headerColumns?.length) {
      this.headerColumns = [
        'cleared',
        'issue',
        'resolution',
        'post_closing',
        'added_by',
        'added_on',
        'updated_by',
        'updated_on',
        'actions',
      ];

      if (this.isConditions || this.isCompleteConditions) {
        this.headerColumns = [
          ...this.headerColumns.slice(0, 4),
          'department',
          ...this.headerColumns.slice(4),
        ];
      }
    }
    this.setUpData();
  }
  setUpData(): void {
    this.tableSource.filter = this.approvalType.id?.toString() ?? '';
    this.tableSource.filterPredicate = this.filterByApproval.bind(this);
    this.itemsSub = this.service.committeeItems$[this.projectId].subscribe(
      (items) => {

        this.tableSource.data = items
          .map(item =>{
            return {...item, permissions:this.hasPermissions(item), extendedPermissions: this.hasExtendedPermissions(item)};
          })
          .sort((a: Mutable<CommitteeItem>, b: Mutable<CommitteeItem>) => {
            if (a.created && b.created && a.created !== b.created) {
              return a.created > b.created ? 1 : -1;
            }
            return 0;
          })
          .sort((a: Mutable<CommitteeItem>, b: Mutable<CommitteeItem>) => {
            let aIndex = this.milestoneIndeces[a.milestone_id ?? '_'] ?? 100;
            let bIndex = this.milestoneIndeces[b.milestone_id ?? '_'] ?? 100;

            return aIndex - bIndex;
          });
        this.legalPermissions = this.getLegalPermissions()
      }
    );
  }
  destroyData(): void {
    this.itemsSub && this.itemsSub.unsubscribe();
  }
  ngOnDestroy() {
    this.destroyData();
  }
  hasPermissions(item:CommitteeItem){
    let approval = this.approvalTypes?.find(type => type.id === item.committee_approval_type_id);
    if(!this.approvalTypes?.length){
      approval = this.approvalType;
    }
    return !!approval?.permissions?._edit;
  }
  hasExtendedPermissions(item:CommitteeItem){
    let approval = this.approvalTypes?.find(type => type.id === item.committee_approval_type_id);
    if (approval?.name?.toLowerCase() === 'executive' || approval?.name?.toLowerCase()==='refresh') {
      let canEditRelated = !!this.approvalTypes?.some((type) => isRelatedToApprovalType(item, type.id) && !!type.permissions?._edit);
      return this.hasPermissions(item) || canEditRelated;
    }
    return this.hasPermissions(item);
  }
  filterByApproval(data: Mutable<CommitteeItem>, filter: string) {
    let executive = this.approvalTypes?.find((type) => type.name?.toLowerCase() === 'executive');
    let refresh = this.approvalTypes?.find((type) => type.name?.toLowerCase() === 'refresh')

    let condition = `${data.committee_approval_type_id}` === `${filter}`;
    if(executive || refresh){
      let isExecutiveOrRefresh = `${data.committee_approval_type_id}` === `${executive?.id}` || `${data.committee_approval_type_id}` === `${refresh?.id}`;
      condition = condition || (isExecutiveOrRefresh && isRelatedToApprovalType(data, filter));
    }
    if (this.isLegalNote) {
      return !!data.is_legal_note && condition;
    }
    if (this.isCompleteConditions) {
      return !!(
        !data.is_legal_note &&
        data.is_condition &&
        data.cleared &&
        data.milestone_id === this.milestone.id
      );
    }
    if (this.isConditions && (this.milestone === undefined || this.milestone.name!=='Closed')) {
      return !!(
        !data.is_legal_note &&
        data.is_condition &&
        (!data.cleared || !!!data.milestone_id)
      );
    }
    if(this.isConditions && (this.milestone!==undefined && this.milestone.name=='Closed')){
      return !!(!data.is_legal_note && data.is_condition)
    }

    return condition && !data.is_legal_note;
  }
  updatePostClosing(value: boolean, projectId: string, item: CommitteeItem) {
    let newItem = { ...item, is_post_closing: value };
    this.service.saveCommitteeItem(projectId, newItem);
  }
  updateCleared(value: boolean, projectId: string, item: CommitteeItem) {
    let newItem = { ...item, cleared: value };
    this.service.saveCommitteeItem(projectId, newItem, () => {
      if (this.milestoneContext) {
        this.approvalService.requestApprovals(projectId);
      }
    });
  }
  getFormattedDate(date?: string): string {
    return date ? moment(date).format('MM/DD/YYYY') : '';
  }

  onOpenChange(opened:boolean, item:CommitteeItem){
    if(opened){
      this.relatedDepartmentSelections.set(item.id ?? '', [...(item.relatedApprovalTypes ?? [])])
    } else if(item.id && this.relatedDepartmentSelections.has(item.id)){
      // Push the final selection into the shared store once the panel closes so every department
      // section re-filters. Doing it per selection would rebuild the table rows and close the panel.
      this.service.updateItem(this.projectId, {
        id: item.id,
        relatedApprovalTypes: this.relatedDepartmentSelections.get(item.id),
      } as CommitteeItem)
    }
  }
  compareApprovalById(a: {id: number}, b: {id: number}): boolean {
    return a && b ? a.id === b.id : a===b
  }
  handleDepartmentChange(value: string, item: CommitteeItem) {
    let newItem = {
      ...item,
      committee_approval_type_id: parseInt(value),
    } as Mutable<CommitteeItem>;
    this.service.saveCommitteeItem(this.projectId, newItem);
  }
  handleRelatedChange(value: MatSelectChange, item: CommitteeItem) {
    const key = item.id ?? ''
    const newSelection: CommitteeApprovalType[] = value.value ?? []
    const prevSelection = this.relatedDepartmentSelections.get(key) ?? []

    const added = newSelection.filter((current:any) => !prevSelection.some((prev:any) => this.compareApprovalById(prev, current)))
    const removed = prevSelection.filter((prev:any) => !newSelection.some((current:any) => this.compareApprovalById(prev, current)))

    // The panel stays open between selections, so keep the snapshot in sync after every change
    // rather than only when the panel opens. Otherwise earlier additions get re-inserted.
    const nextSelection = [
      ...prevSelection.filter((prev) => !removed.includes(prev)),
      ...added.map((approval) => ({...approval})),
    ]
    this.relatedDepartmentSelections.set(key, nextSelection)

    for(const approval of removed){
      if(approval.committeeItemRelationId){
        this.service.deleteItemRelatedApproval(approval.committeeItemRelationId).subscribe()
      }
    }
    for(const approval of nextSelection.slice(nextSelection.length - added.length)){
      this.service.addItemRelatedApproval(item, approval.id).subscribe((result: any) => {
        // Remember the new relation id so this department can be removed without reopening the panel
        approval.committeeItemRelationId = result?.data?.item?.id
      })
    }
    // let newItem = {
    //   ...item,
    //   relatedApprovalTypes: newSelection,
    // } as Mutable<CommitteeItem>;
    // this.service.saveCommitteeItem(this.projectId, newItem);
  }
  handleMilestoneChange(value: string, item: CommitteeItem) {
    let newItem = { ...item, milestone_id: value } as Mutable<CommitteeItem>;
    this.service.saveCommitteeItem(this.projectId, newItem);
  }
  isArray(info: any): boolean {
    return Array.isArray(info);
  }
  getLegalPermissions(): boolean {
    let legalApproval = this.service.getLegalApprovalType()?.id;
    if (legalApproval) {
      return this.permsService.userPerms['COMMITTEEAPPROVALTYPE'][
        legalApproval
      ]['_edit'];
    }
    return false;
  }
  saveIssue(projectId: string, item: CommitteeItem, milestone?:string ) {
    let uncleared: CommitteeItem = {...item, cleared:false};
    if(milestone){
      this.service.saveCommitteeItem(projectId, uncleared, undefined, milestone);
    }else{
      this.service.saveCommitteeItem(projectId, uncleared);
    }
  }
  isString(info: any): boolean {
    return typeof info === 'string';
  }
}
